/**
 * useDraggableWindow: deixa a janelinha da chamada ser arrastada para qualquer ponto da tela (mouse,
 * toque e setas do teclado) pelo cabeçalho. Só vale com `enabled` (a sala fora da guia): a janela normal
 * fica encaixada no canto, como sempre. Antes do primeiro arraste não há posição própria (o encaixe é do
 * CSS); depois dele a posição é guardada e continua para a próxima vez que a janela for minimizada.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Distância mínima entre a janela e a borda da tela. */
export const WINDOW_MARGIN_PX = 8;
export const KEY_STEP_PX = 24;

const KEY_DELTAS = {
    ArrowLeft: [-KEY_STEP_PX, 0],
    ArrowRight: [KEY_STEP_PX, 0],
    ArrowUp: [0, -KEY_STEP_PX],
    ArrowDown: [0, KEY_STEP_PX],
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/** Mantém a janela inteira dentro da tela, com a margem; janela maior que a tela fica no canto de cima. */
export function clampToViewport({ left, top }, { width, height }, viewport) {
    return {
        left: clamp(left, WINDOW_MARGIN_PX, Math.max(WINDOW_MARGIN_PX, viewport.width - width - WINDOW_MARGIN_PX)),
        top: clamp(top, WINDOW_MARGIN_PX, Math.max(WINDOW_MARGIN_PX, viewport.height - height - WINDOW_MARGIN_PX)),
    };
}

/** Cliques nos botões do cabeçalho não são arraste. */
const isInteractive = (target) => Boolean(target?.closest?.('button, a, input, select, textarea, [role="button"]'));

/**
 * @param {React.RefObject<HTMLElement>} windowRef a janela que se move
 * @param {{ enabled: boolean }} options
 * @returns {{ style: object | undefined, dragging: boolean, handlers: object }} `handlers` vão no cabeçalho
 */
export function useDraggableWindow(windowRef, { enabled }) {
    const [position, setPosition] = useState(null); // { left, top } em px; null = encaixe do CSS
    const [dragging, setDragging] = useState(false);
    const drag = useRef(null);

    const viewport = () => ({ width: window.innerWidth, height: window.innerHeight });
    const sizeOf = (rect) => ({ width: rect.width, height: rect.height });

    const onPointerDown = (event) => {
        if (!enabled || event.button !== 0 || !windowRef.current || isInteractive(event.target)) return;
        const rect = windowRef.current.getBoundingClientRect();
        drag.current = { pointerId: event.pointerId, offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top, size: sizeOf(rect) };
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDragging(true);
    };

    const onPointerMove = (event) => {
        const d = drag.current;
        if (!d || event.pointerId !== d.pointerId) return;
        setPosition(clampToViewport({ left: event.clientX - d.offsetX, top: event.clientY - d.offsetY }, d.size, viewport()));
    };

    const endDrag = (event) => {
        if (!drag.current || event.pointerId !== drag.current.pointerId) return;
        event.currentTarget.releasePointerCapture?.(event.pointerId);
        drag.current = null;
        setDragging(false);
    };

    const onKeyDown = (event) => {
        const delta = KEY_DELTAS[event.key];
        if (!enabled || !delta || event.target !== event.currentTarget || !windowRef.current) return;
        event.preventDefault();
        const rect = windowRef.current.getBoundingClientRect();
        setPosition((current) => {
            const from = current ?? { left: rect.left, top: rect.top };
            return clampToViewport({ left: from.left + delta[0], top: from.top + delta[1] }, sizeOf(rect), viewport());
        });
    };

    // A tela encolheu (ou o navegador mudou de tamanho): a janela não pode ficar para fora.
    const reclamp = useCallback(() => {
        const el = windowRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        setPosition((current) => (current ? clampToViewport(current, sizeOf(rect), viewport()) : current));
    }, [windowRef]);

    useEffect(() => {
        if (!enabled || !position) return undefined;
        window.addEventListener('resize', reclamp);
        return () => window.removeEventListener('resize', reclamp);
    }, [enabled, position, reclamp]);

    return {
        style: enabled && position ? { left: position.left, top: position.top, right: 'auto', bottom: 'auto' } : undefined,
        dragging,
        handlers: { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag, onKeyDown },
    };
}
